import React, { useEffect, useState, useCallback } from "react";
import moment from "moment";
import { useTranslation } from 'react-i18next';
import { useStoreState } from 'easy-peasy';
import { useAuthContext } from "contexts/AuthContext"
import usePermissions from "hooks/usePermissions";
import WriteButton from "components/Buttons/WriteButton/WriteButton";
import { useAppContext } from "contexts/AppContext"

import { Button, Modal, Typography } from "stories/components";
import { TableAuto } from "stories/tables"

import { httpGetTransactions } from "services/transactions"
import { thousandFormat, roundScaleDecimals } from "services/helpers"
import { renderTransactionTypeBadge } from "utils/transactionTypeBadge"
import { INTEREST_TYPES } from "utils/interestTransactions"
import InterestTransactionEdit from "views/app/InterestTransactionEdit/InterestTransactionEdit"

// Lainan Korkotuotot-välilehti: lainan korkokirjaukset (tyypit 33/34) kaikista tilin salkuista.
// Lista luetaan suoraan tapahtumista; erillistä Dividend-riviä ei ole.

const InterestTransactionsList = ({ filters, related_model_type, setAddSplit }) => {
  const { accountUserType } = useAuthContext()
  const { canWrite } = usePermissions();
  const { getTransactionType } = useAppContext()
  const { t } = useTranslation();
  const portfolios = useStoreState((state) => state.portfolio?.portfolios);

  const [loading, setLoading] = useState(false)
  const [objects, setObjects] = useState([])
  const [count, setCount] = useState(0)

  const getObjects = () => {
    const related_model_id = filters?.private_investment_fund;
    if (!related_model_id || related_model_id === "") {
      return;
    }
    setLoading(true)
    httpGetTransactions({
      related_model_type: related_model_type,
      related_model_id: related_model_id,
      type__in: INTEREST_TYPES.join(","),
      ordering: "-date_transaction",
      limit: 500,
      offset: 0,
    }).then(res => {
      const rows = res?.data?.results || (Array.isArray(res?.data) ? res.data : [])
      setObjects(rows)
      setCount(res?.data?.count ?? rows.length)
    }).finally(() => {
      setLoading(false)
    })
  }

  useEffect(() => {
    getObjects()
  }, [filters?.private_investment_fund])

  const headers = [
    { label: t("interest_list_notes", "Selite"), key: "notes" },
    { label: t("interest_list_pay_date", "Maksupäivä"), key: "date_transaction" },
    { label: t("interest_list_type", "Tyyppi"), key: "type_badge" },
    { label: t("interest_list_portfolio", "Salkku"), key: "portfolio_name" },
    { label: t("interest_list_amount", "Korko yhteensä"), key: "amount", className: "text-right" },
    { label: t("interest_list_currency", "Valuutta"), key: "currency_label" },
    { label: t("interest_list_actions", " "), key: "actions" },
  ];

  // listan serializer palauttaa salkun objektina ({id, name}); varaudutaan myös pelkkään id:hen
  const getPortfolioName = useCallback((value) => {
    if (value && typeof value === "object") {
      return value.name || value.id;
    }
    const portfolio = portfolios && portfolios.find(p => p.id === value);
    return portfolio ? portfolio.name : value;
  }, [portfolios])

  const getTableRows = useCallback(() => {
    return objects && objects.map((row) => {
      return {
        ...row,
        notes: row?.notes || "",
        date_transaction: moment(row.date_transaction).format('DD.MM.YYYY'),
        type_badge: renderTransactionTypeBadge(row?.type, t, getTransactionType),
        portfolio_name: getPortfolioName(row?.portfolio),
        amount: <div className="text-right">{thousandFormat(roundScaleDecimals(parseFloat(row?.value_total), 2))}</div>,
        actions: (
          <div className="d-flex justify-content-end">
            <WriteButton className="btn-sm" color="secondary" onClick={() => openModalEdit(row?.id)}>{t("Muokkaa")}</WriteButton>
          </div>
        ),
      }
    })
  }, [objects, portfolios])

  const total = objects.reduce((sum, row) => sum + parseFloat(row?.value_total || 0), 0);

  // Modal: New/Edit
  const [modalEditOpen, setModalEditOpen] = useState(false)
  const [modalEditId, setModalEditId] = useState()

  const toggleModalEdit = () => {
    setModalEditOpen(s => !s);
  }

  const openModalEdit = (id) => {
    setModalEditOpen(true)
    setModalEditId(id);
  }

  const modalEditResolve = () => {
    setModalEditOpen(false);
    setModalEditId(null);
    getObjects()
  }

  const modalEditReject = () => {
    setModalEditOpen(false);
    setModalEditId(null);
  }

  useEffect(() => {
    setAddSplit(
      <div className="d-flex justify-content-end align-bottom">
        <Button color="secondary" onClick={() => openModalEdit(null)}>{t("new_interest_button", "Uusi korkokirjaus")}</Button>
      </div>
    )
    return () => {
      setAddSplit(null)
    };
  }, [])

  return (
    <>
      <div className="d-flex mb-2">
        <Typography variant="small" className="text-muted">
          {t("interest_list_count", "{{count}} kirjausta", { count: count })}
          {objects.length > 0 ? ` · ${t("interest_list_total", "yhteensä")} ${thousandFormat(roundScaleDecimals(total, 2))} ${objects[0]?.currency_label || ""}` : ""}
        </Typography>
      </div>

      <TableAuto
        showRowNumber={true}
        showId={false}
        checkboxes={false}
        headers={headers}
        headerColor={"light"}
        rows={getTableRows()}
        loading={loading}
        pagination={false}
        paginationOffset={0}
      />

      <Modal
        title={t("interest_edit_title", "Korkokirjaus")}
        isOpen={modalEditOpen}
        toggleModal={toggleModalEdit}
      >
        <InterestTransactionEdit
          related_model_id={filters?.private_investment_fund}
          related_model_type={related_model_type}
          objectId={modalEditId}
          modalOpen={modalEditOpen}
          handleResolve={modalEditResolve}
          handleReject={modalEditReject}
          currency_label={filters?.currency?.content || filters?.currency_label || filters?.currency}
        />
      </Modal>
    </>
  );
}

export default InterestTransactionsList;
